import { Button, Chip, Modal, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useState } from "react";
import { rolesData } from "@legal-500/permissions";
import {
  userManagement,
  type FirmFeaturesSource,
  type ProductScopeRestriction,
  type TemporalFeature,
  type UserRoleRow,
} from "../../../api/userManagement.service";
import { cxiReportGrants, type CxiGrantPracticeArea } from "../../../api/cxiReportGrants.service";
import {
  SearchableMultiSelect,
  type SelectOption,
} from "../../../components/Form/SearchableSelect";
import LoadingState from "../../../components/LoadingState";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { ScopeTreePicker } from "../components/ScopeTreePicker";
import { buildScopeOptions } from "../utils/resolveScopeLabel";
import styles from "../UserManagement.module.css";

const ACCOUNT_SERVICE_ROLES = rolesData
  .filter(role => role.context === "account-services")
  .sort((roleA, roleB) => roleA.name.localeCompare(roleB.name));

const CXI_ROLE = "cxi_report.user";
const HISTORICAL_DATA_ROLE = "historical_data.user";

const ROLE_TO_SCOPED_PRODUCT: Record<string, string> = {
  "sars.user": "sars",
  "sars.admin": "sars",
  "live.user": "live",
  "referees.user": "referee_track",
  "referees.admin": "referee_track",
  "profiles.user": "profiles",
  "profiles.admin": "profiles",
};

const SCOPED_PRODUCT_LABELS: Record<string, string> = {
  sars: "Submissions Analysis (SARS)",
  live: "Legal 500 Live",
  referee_track: "Referee Track & Add",
  profiles: "Profiles",
};

function scopedProductLabel(productKey: string): string {
  return SCOPED_PRODUCT_LABELS[productKey] ?? productKey;
}

const ALWAYS_GRANTABLE = new Set([
  "account_owner",
  "submissions.user",
  "submissions.admin",
  "cxi_report.user",
  "marketing.user",
  "profiles.user",
  "profiles.admin",
  "nominations.user",
]);

function isFirmEntitled(roleName: string, firmFeatures: TemporalFeature[]): boolean {
  if (ALWAYS_GRANTABLE.has(roleName)) return true;
  const hasFeature = (code: string) => firmFeatures.some(feature => feature.feature === code);
  if (roleName === "live.user") return hasFeature("live");
  if (roleName === "referees.user" || roleName === "referees.admin") {
    return hasFeature("referee_track");
  }
  if (roleName === "sars.user" || roleName === "sars.admin") return hasFeature("sars");
  if (roleName === HISTORICAL_DATA_ROLE) return hasFeature("historical_data");
  return false;
}

function formatPracticeAreaNames(
  practiceAreaIds: number[],
  options: CxiGrantPracticeArea[],
): string {
  if (practiceAreaIds.length === 0) return "No practice areas";
  const optionById = new Map(options.map(option => [option.id, option]));
  return practiceAreaIds
    .map(id => {
      const option = optionById.get(id);
      if (!option) return `#${id}`;
      return option.country
        ? `${option.name} (${option.country}) · #${option.id}`
        : `${option.name} · #${option.id}`;
    })
    .join(", ");
}

function buildPracticeAreaSelectOptions(options: CxiGrantPracticeArea[]): SelectOption[] {
  return options.map(option => ({
    value: option.id,
    label: option.country
      ? `${option.name} — ${option.country} · #${option.id}`
      : `${option.name} · #${option.id}`,
  }));
}

type RoleSourceBadgeProps = {
  source: "reconcile" | "manual";
};

function RoleSourceBadge({ source }: RoleSourceBadgeProps) {
  if (source === "reconcile") {
    return (
      <span
        className={`${styles.statusBadge} ${styles.statusAuto}`}
        title="Derived from the data-mart / legacy / UPE by the entitlement reconcile"
      >
        Data-mart / automated
      </span>
    );
  }
  return (
    <span
      className={`${styles.statusBadge} ${styles.statusManual}`}
      title="Set by staff; protected from the reconcile"
    >
      Manually set
    </span>
  );
}

type CxiPracticeAreaModalProps = {
  mode: "add" | "edit";
  initialPracticeAreaIds: number[];
  practiceAreaOptions: CxiGrantPracticeArea[];
  optionsUnavailable: boolean;
  isLoadingOptions: boolean;
  onConfirm: (practiceAreaIds: number[]) => Promise<void>;
  onClose: () => void;
};

function CxiPracticeAreaModal({
  mode,
  initialPracticeAreaIds,
  practiceAreaOptions,
  optionsUnavailable,
  isLoadingOptions,
  onConfirm,
  onClose,
}: CxiPracticeAreaModalProps) {
  const [practiceAreaIds, setPracticeAreaIds] = useState<number[]>(initialPracticeAreaIds);
  const [isSaving, setIsSaving] = useState(false);

  const selectOptions = useMemo(
    () => buildPracticeAreaSelectOptions(practiceAreaOptions),
    [practiceAreaOptions],
  );

  async function handleConfirm() {
    setIsSaving(true);
    try {
      await onConfirm(practiceAreaIds);
    } finally {
      setIsSaving(false);
    }
  }

  const title =
    mode === "edit"
      ? "Edit cxi_report.user — practice areas"
      : "Add cxi_report.user — practice areas";

  const selectDisabled = isSaving || isLoadingOptions || optionsUnavailable;
  const confirmDisabled =
    isSaving || isLoadingOptions || optionsUnavailable || practiceAreaIds.length === 0;

  function selectHelperText(): string | undefined {
    if (optionsUnavailable) return "Practice-area list unavailable — try again later";
    if (isLoadingOptions) return "Loading practice areas…";
    if (selectOptions.length === 0) {
      return "No practice areas found for this firm — the firm may not have any published rankings yet.";
    }
    return undefined;
  }

  return (
    <Modal isOpen title={title} onClose={onClose} size="small">
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
        <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
          Select at least one practice area this user should have CXI access to.
        </p>

        <SearchableMultiSelect
          name="cxi-user-practice-areas"
          label="Practice areas"
          value={practiceAreaIds}
          options={selectOptions}
          onChange={setPracticeAreaIds}
          placeholder="Select practice areas…"
          disabled={selectDisabled}
          helperText={selectHelperText()}
        />

        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleConfirm()}
            disabled={confirmDisabled}
          >
            {isSaving ? "Saving…" : mode === "edit" ? "Save" : "Add"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type CxiPracticeAreaOptionsState = {
  status: "idle" | "loading" | "loaded" | "error";
  options: CxiGrantPracticeArea[];
};

const INITIAL_CXI_PRACTICE_AREA_OPTIONS_STATE: CxiPracticeAreaOptionsState = {
  status: "idle",
  options: [],
};

type ScopeRestrictionModalProps = {
  productLabel: string;
  firmScopeIds: string[] | null;
  initialScopeIds: string[];
  hasExistingRestriction: boolean;
  onSave: (scopeIds: string[]) => Promise<void>;
  onClear: () => Promise<void>;
  onClose: () => void;
};

function ScopeRestrictionModal({
  productLabel,
  firmScopeIds,
  initialScopeIds,
  hasExistingRestriction,
  onSave,
  onClear,
  onClose,
}: ScopeRestrictionModalProps) {
  const [scopeIds, setScopeIds] = useState<string[]>(initialScopeIds);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const isBusy = isSaving || isClearing;

  async function handleSave() {
    setIsSaving(true);
    try {
      await onSave(scopeIds);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleClear() {
    setIsClearing(true);
    try {
      await onClear();
    } finally {
      setIsClearing(false);
    }
  }

  return (
    <Modal
      isOpen
      title={`Restrict ${productLabel} — countries/regions`}
      onClose={onClose}
      size="medium"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
        <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
          Select the countries/regions this user should be restricted to for {productLabel}. Use
          Clear restriction to remove the restriction and keep this user firm-wide.
        </p>

        <ScopeTreePicker
          firmScopeIds={firmScopeIds}
          value={scopeIds}
          onChange={setScopeIds}
          disabled={isBusy}
        />

        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          {hasExistingRestriction && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => void handleClear()}
              disabled={isBusy}
            >
              {isClearing ? "Clearing…" : "Clear restriction"}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSave()}
            disabled={isBusy || scopeIds.length === 0}
          >
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type UserRolesManagerProps = {
  userId: string;
  organisationId: string;
  organisationName: string;
};

export function UserRolesManager({
  userId,
  organisationId,
  organisationName,
}: UserRolesManagerProps) {
  const [roleRows, setRoleRows] = useState<UserRoleRow[]>([]);
  const [firmFeatures, setFirmFeatures] = useState<TemporalFeature[]>([]);
  const [firmFeaturesSource, setFirmFeaturesSource] = useState<FirmFeaturesSource>("current");
  const [scopesByFeature, setScopesByFeature] = useState<Record<string, string[]>>({});
  const [scopeRestrictions, setScopeRestrictions] = useState<ProductScopeRestriction[]>([]);
  const [cxiPracticeAreaIds, setCxiPracticeAreaIds] = useState<number[]>([]);
  const [cxiPracticeAreaOptionsState, setCxiPracticeAreaOptionsState] =
    useState<CxiPracticeAreaOptionsState>(INITIAL_CXI_PRACTICE_AREA_OPTIONS_STATE);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const [cxiModalMode, setCxiModalMode] = useState<"add" | "edit" | null>(null);
  const [scopeEditorProduct, setScopeEditorProduct] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    void loadData();
    setCxiPracticeAreaOptionsState(INITIAL_CXI_PRACTICE_AREA_OPTIONS_STATE);
  }, [userId, organisationId]);

  async function loadData() {
    setIsLoading(true);
    try {
      const [rolesResult, featuresResult, scopeRestrictionsResult, cxiGrantResult] =
        await Promise.allSettled([
          userManagement.getUserRoles(userId),
          userManagement.getOrganisationFeatureStates(organisationId),
          userManagement.getUserProductScopeRestrictions(userId, organisationId),
          cxiReportGrants.getForUser(organisationId, userId),
        ]);

      const failedLoads: string[] = [];

      if (rolesResult.status === "fulfilled") {
        setRoleRows(rolesResult.value.filter(row => row.organisationId === organisationId));
      } else {
        failedLoads.push("roles");
      }

      if (featuresResult.status === "fulfilled") {
        setFirmFeatures(featuresResult.value.temporalFeatures);
        setFirmFeaturesSource(featuresResult.value.source);
        setScopesByFeature(featuresResult.value.scopesByFeature);
      } else {
        failedLoads.push("firm feature states");
      }

      if (scopeRestrictionsResult.status === "fulfilled") {
        setScopeRestrictions(scopeRestrictionsResult.value);
      } else {
        failedLoads.push("scope restrictions");
      }

      if (cxiGrantResult.status === "fulfilled") {
        setCxiPracticeAreaIds(cxiGrantResult.value?.practiceAreaIds ?? []);
      } else {
        failedLoads.push("CXI practice area grant");
      }

      if (failedLoads.length > 0) {
        setToast({
          message: `Failed to load ${failedLoads.join(", ")} — some roles may not appear as configurable`,
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function refreshRoleRows(): Promise<void> {
    const refreshed = await userManagement.getUserRoles(userId);
    setRoleRows(refreshed.filter(row => row.organisationId === organisationId));
  }

  async function ensureCxiPracticeAreaOptionsLoaded() {
    if (
      cxiPracticeAreaOptionsState.status === "loaded" ||
      cxiPracticeAreaOptionsState.status === "loading"
    ) {
      return;
    }

    setCxiPracticeAreaOptionsState({ status: "loading", options: [] });
    try {
      const options = await cxiReportGrants.getPracticeAreaOptions(organisationId);
      setCxiPracticeAreaOptionsState({ status: "loaded", options });
    } catch {
      setCxiPracticeAreaOptionsState({ status: "error", options: [] });
    }
  }

  function openCxiModal(mode: "add" | "edit") {
    setCxiModalMode(mode);
    void ensureCxiPracticeAreaOptionsLoaded();
  }

  async function handleAddRole(role: string) {
    setPendingRole(role);
    try {
      await userManagement.addUserRole(userId, role, organisationId);
      await refreshRoleRows();
      setToast({ message: `Role ${role} added`, type: "success" });
    } catch (err) {
      setToast({
        message: `Failed to add role: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setPendingRole(null);
    }
  }

  async function handleAddCxiRole(practiceAreaIds: number[]) {
    setCxiModalMode(null);
    setPendingRole(CXI_ROLE);
    try {
      await userManagement.addUserRole(userId, CXI_ROLE, organisationId);
      try {
        await cxiReportGrants.upsert(organisationId, userId, practiceAreaIds);
        setCxiPracticeAreaIds(practiceAreaIds);
      } catch {
        setToast({
          message: `Role ${CXI_ROLE} added, but practice areas could not be saved`,
          type: "error",
        });
        await refreshRoleRows();
        return;
      }
      await refreshRoleRows();
      setToast({ message: `Role ${CXI_ROLE} added`, type: "success" });
    } catch (err) {
      setToast({
        message: `Failed to add role: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setPendingRole(null);
    }
  }

  async function handleUpdateCxiGrant(practiceAreaIds: number[]) {
    setCxiModalMode(null);
    setPendingRole(CXI_ROLE);
    try {
      await cxiReportGrants.upsert(organisationId, userId, practiceAreaIds);
      setCxiPracticeAreaIds(practiceAreaIds);
      setToast({ message: "CXI practice areas updated", type: "success" });
    } catch (err) {
      setToast({
        message: `Failed to update practice areas: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setPendingRole(null);
    }
  }

  async function handleRemoveRole(role: string) {
    setPendingRole(role);
    try {
      await userManagement.removeUserRole(userId, role, organisationId);
      await refreshRoleRows();
      if (role === CXI_ROLE) setCxiPracticeAreaIds([]);
      setToast({ message: `Role ${role} removed`, type: "success" });
    } catch (err) {
      setToast({
        message: `Failed to remove role: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    } finally {
      setPendingRole(null);
    }
  }

  function resolveRoleRow(roleName: string): UserRoleRow | undefined {
    return roleRows.find(row => row.role === roleName);
  }

  function resolveScopeRestriction(productKey: string): ProductScopeRestriction | undefined {
    return scopeRestrictions.find(restriction => restriction.product === productKey);
  }

  function chipLabelForProduct(productKey: string): string {
    const restriction = resolveScopeRestriction(productKey);
    if (!restriction || restriction.scope_ids.length === 0) return "Firm-wide";
    const labels = buildScopeOptions(restriction.scope_ids).map(option => option.label);
    return `Restricted: ${labels.join(", ")}`;
  }

  /** Bounds the tree to the firm's fulfilment-side entitlement for this product; null (unbounded)
   * when feature-states aren't fulfilment-sourced or the firm has no known scope for it. */
  function firmScopeIdsForProduct(productKey: string): string[] | null {
    if (firmFeaturesSource !== "fulfilment") return null;
    const rawScopeIds = scopesByFeature[productKey];
    return rawScopeIds && rawScopeIds.length > 0 ? rawScopeIds : null;
  }

  async function handleSaveScopeRestriction(productKey: string, scopeIds: string[]) {
    try {
      await userManagement.setUserProductScopeRestriction(
        userId,
        organisationId,
        productKey,
        scopeIds,
      );
      setScopeRestrictions(prev => [
        ...prev.filter(restriction => restriction.product !== productKey),
        { product: productKey, scope_ids: scopeIds },
      ]);
      setToast({
        message: `Country/region restriction saved for ${scopedProductLabel(productKey)}`,
        type: "success",
      });
      setScopeEditorProduct(null);
    } catch (err) {
      setToast({
        message: `Failed to save restriction: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    }
  }

  async function handleClearScopeRestriction(productKey: string) {
    try {
      await userManagement.deleteUserProductScopeRestriction(userId, organisationId, productKey);
      setScopeRestrictions(prev => prev.filter(restriction => restriction.product !== productKey));
      setToast({
        message: `Restriction cleared for ${scopedProductLabel(productKey)} — user now uses the firm's full entitled scope`,
        type: "success",
      });
      setScopeEditorProduct(null);
    } catch (err) {
      setToast({
        message: `Failed to clear restriction: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    }
  }

  if (isLoading) return <LoadingState />;

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <h4
        style={{
          fontSize: "var(--font-size-sm)",
          fontWeight: "var(--font-weight-semibold)",
          color: "var(--color-text-secondary)",
          marginBottom: "0.5rem",
        }}
      >
        Permissions — {organisationName}
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {ACCOUNT_SERVICE_ROLES.map(roleDef => {
          const existingRow = resolveRoleRow(roleDef.name);
          const isAssigned = Boolean(existingRow);
          const isReconcileOwned = existingRow?.source === "reconcile";
          const isBusy = pendingRole === roleDef.name;
          const canAdd = isFirmEntitled(roleDef.name, firmFeatures);
          const isCxiRole = roleDef.name === CXI_ROLE;
          const scopedProduct = ROLE_TO_SCOPED_PRODUCT[roleDef.name];

          return (
            <div
              key={roleDef.name}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                padding: "0.375rem 0",
                borderBottom: "1px solid var(--color-border)",
                opacity: isReconcileOwned ? 0.75 : 1,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span
                    className={styles.permissionTag}
                    style={{ fontFamily: "monospace", flexShrink: 0 }}
                  >
                    {roleDef.name}
                  </span>
                  {existingRow && <RoleSourceBadge source={existingRow.source} />}
                </div>
                {roleDef.description && (
                  <span
                    style={{
                      fontSize: "var(--font-size-xs, 0.7rem)",
                      color: "var(--color-text-secondary)",
                      display: "block",
                      marginTop: "0.125rem",
                    }}
                  >
                    {roleDef.description}
                  </span>
                )}
                {isCxiRole && isAssigned && (
                  <span
                    style={{
                      fontSize: "var(--font-size-xs, 0.7rem)",
                      color: "var(--color-text-secondary)",
                      display: "block",
                      marginTop: "0.25rem",
                    }}
                  >
                    Practice areas:{" "}
                    {formatPracticeAreaNames(
                      cxiPracticeAreaIds,
                      cxiPracticeAreaOptionsState.options,
                    )}
                  </span>
                )}
              </div>
              <PermissionGate permission="user.write_accounts">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}
                >
                  {isAssigned && scopedProduct && (
                    <Chip
                      variant="outlined"
                      color={resolveScopeRestriction(scopedProduct) ? "warning" : "default"}
                      size="small"
                      onClick={() => setScopeEditorProduct(scopedProduct)}
                      disabled={isBusy}
                    >
                      {chipLabelForProduct(scopedProduct)}
                    </Chip>
                  )}
                  {!isAssigned && canAdd && (
                    <Button
                      variant="outlined"
                      color="default"
                      size="small"
                      onClick={() =>
                        isCxiRole ? openCxiModal("add") : void handleAddRole(roleDef.name)
                      }
                      disabled={isBusy}
                    >
                      {isBusy ? "Adding…" : "Add"}
                    </Button>
                  )}
                  {isAssigned && isCxiRole && (
                    <Button
                      variant="outlined"
                      color="default"
                      size="small"
                      onClick={() => openCxiModal("edit")}
                      disabled={isBusy}
                    >
                      Edit
                    </Button>
                  )}
                  {isAssigned && !isReconcileOwned && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => void handleRemoveRole(roleDef.name)}
                      disabled={isBusy}
                    >
                      {isBusy ? "Removing…" : "Remove"}
                    </Button>
                  )}
                </div>
              </PermissionGate>
            </div>
          );
        })}
      </div>

      {cxiModalMode && (
        <CxiPracticeAreaModal
          mode={cxiModalMode}
          initialPracticeAreaIds={cxiModalMode === "edit" ? cxiPracticeAreaIds : []}
          practiceAreaOptions={cxiPracticeAreaOptionsState.options}
          optionsUnavailable={cxiPracticeAreaOptionsState.status === "error"}
          isLoadingOptions={cxiPracticeAreaOptionsState.status === "loading"}
          onConfirm={cxiModalMode === "edit" ? handleUpdateCxiGrant : handleAddCxiRole}
          onClose={() => setCxiModalMode(null)}
        />
      )}

      {scopeEditorProduct && (
        <ScopeRestrictionModal
          productLabel={scopedProductLabel(scopeEditorProduct)}
          firmScopeIds={firmScopeIdsForProduct(scopeEditorProduct)}
          initialScopeIds={resolveScopeRestriction(scopeEditorProduct)?.scope_ids ?? []}
          hasExistingRestriction={Boolean(resolveScopeRestriction(scopeEditorProduct))}
          onSave={scopeIds => handleSaveScopeRestriction(scopeEditorProduct, scopeIds)}
          onClear={() => handleClearScopeRestriction(scopeEditorProduct)}
          onClose={() => setScopeEditorProduct(null)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default UserRolesManager;
